import React, { FC, ReactNode } from "react";
import "./GenericModal.scss";
import Typhography from "../Typhography/Typhography";
import { CloseButton, Dialog, Portal } from "@chakra-ui/react";

interface IGenericModal {
  isOpen: boolean;
  title?: string;
  classNameBody?: string;
  isLoading?: boolean;
  confirmLabel?: string;
  Footer?: ReactNode;
  children: ReactNode;
  size?: Dialog.RootProps["size"];
  cancelLabel?: string;
  onClose?: () => void;
  onConfirm?: () => void;
  Header?: ReactNode;
  closeOnInteractOutside?: boolean;
}

const GenericModal: FC<IGenericModal> = ({
  isOpen,
  onClose,
  isLoading,
  classNameBody,
  title,
  size = "xl",
  Footer,
  Header,
  children,
  // Default false: react-select / dropdown / picker rendono il menu in
  // portal su document.body, fuori dal Dialog.Content. Con il default true
  // di Chakra il primo pointerdown sull'opzione viene interpretato come
  // "interact outside" → chiude la modal prima che onChange triggeri.
  closeOnInteractOutside = false,
}) => {
  return (
    <Dialog.Root
      lazyMount={true}
      unmountOnExit={true}
      size={size}
      open={isOpen}
      placement={"center"}
      closeOnInteractOutside={closeOnInteractOutside}
      // Chakra/Ark default: trapFocus=true → blocca interazioni con elementi
      // renderizzati in portal (es. menu di react-select) perché vengono
      // intercettati come "outside dialog". Disabilitando il trap il menu
      // riceve correttamente i click sulle option.
      trapFocus={false}
      onOpenChange={(e) => {
        if (!e.open) onClose?.();
      }}
    >
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content
            style={{
              padding: "32px 40px",
              overflow: "hidden",
            }}
          >
            {Header || (
              <Dialog.Header style={{ padding: 0, paddingBottom: "12px" }}>
                <Typhography size="bold-24">{title}</Typhography>
              </Dialog.Header>
            )}
            <Dialog.Body
              className={classNameBody}
              style={{
                padding: 0,
                overflowY: "auto",
                maxHeight: "calc(100vh - 140px)",
              }}
            >
              {children}
            </Dialog.Body>
            {Footer && <Dialog.Footer>{Footer}</Dialog.Footer>}
            <Dialog.CloseTrigger asChild disabled={isLoading}>
              <CloseButton size="sm" />
            </Dialog.CloseTrigger>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
};

export default GenericModal;
